import React, { Suspense, Fragment, lazy } from 'react'
import { HashRouter as Router, Switch, Route, Redirect } from 'react-router-dom'
import { history } from './history'

import Loader from './components/Loader/Loader'
import AdminLayout from './layouts/AdminLayout'
import GuestGuard from './components/Auth/GuestGuard'
import AuthGuard from './components/Auth/AuthGuard'

import { BASE_URL } from './config/constant'

export const renderRoutes = (routes = []) => (
  <Router history={history}>
    <Suspense fallback={<Loader />}>
      <Switch>
        {routes.map((route, i) => {
          if (route.permissions && route.permissions.length > 0) {
            route.guard = AuthGuard
          }
          if (route.routes) {
            route.routes = route.routes.map((item) => {
              if (route.guard) {
                item.guard = route.guard
              }
              return item
            })
          }
          const Guard = route.guard || Fragment
          const Layout = route.layout || Fragment
          const Component = route.component
          return (
            <Route
              key={i}
              path={route.path}
              exact={route.exact}
              render={(props) => (
                <Guard
                  state={{
                    permissions: route?.permissions || [],
                    path: route?.path || null,
                  }}
                >
                  <Layout {...(route.layout ? { routes: route.routes } : {})}>
                    {route.routes ? (
                      renderRoutes(route.routes)
                    ) : (
                      <Component {...props} />
                    )}
                  </Layout>
                </Guard>
              )}
            />
          )
        })}
      </Switch>
    </Suspense>
  </Router>
)

const routes = [
  {
    exact: true,
    guard: GuestGuard,
    path: '/auth/signin-1',
    component: lazy(() => import('./views/auth/signin/SignIn1')),
  },
  {
    exact: true,
    path: '/404',
    guard: GuestGuard,
    component: lazy(() => import('./views/errors/NotFound404')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/maintenance/coming-soon',
    component: lazy(() => import('./views/maintenance/ComingSoon')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/maintenance/error',
    component: lazy(() => import('./views/maintenance/Error')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/maintenance/unauthorized',
    component: lazy(() => import('./views/maintenance/Unauthorized')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/maintenance/offline-ui',
    component: lazy(() => import('./views/maintenance/OfflineUI')),
  },
  {
    exact: true,
    path: '/auth/signup-1',
    component: lazy(() => import('./views/auth/signup/SignUp1')),
  },
  {
    exact: true,
    path: '/auth/signup-2',
    component: lazy(() => import('./views/auth/signup/SignUp2')),
  },
  {
    exact: true,
    path: '/auth/signin-2',
    component: lazy(() => import('./views/auth/signin/SignIn2')),
  },
  {
    exact: true,
    path: '/auth/login/:source',
    component: lazy(() => import('./views/auth/signin/AppLogin')),
  },
  {
    exact: true,
    path: '/auth/reset-password-1',
    component: lazy(() => import('./views/auth/reset-password/ResetPassword1')),
  },
  {
    exact: true,
    path: '/auth/reset-password-2',
    component: lazy(() => import('./views/auth/reset-password/ResetPassword2')),
  },
  {
    exact: true,
    path: '/auth/change-password',
    component: lazy(() => import('./views/auth/ChangePassword')),
  },
  {
    exact: true,
    path: '/auth/profile-settings',
    component: lazy(() => import('./views/auth/ProfileSettings')),
  },
  {
    exact: true,
    path: '/auth/tabs-auth',
    component: lazy(() => import('./views/auth/TabsAuth')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/print/report',
    component: lazy(() =>
      import('./views/panels/hospital/ClassicReport/PrintReport'),
    ),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/public/telemed/:code',
    component: lazy(() => import('./views/panels/hospital/Telemed/PublicCall')),
  },
  {
    path: '/patient-file',
    layout: AdminLayout,
    guard: AuthGuard,
    routes: [
      {
        exact: true,
        path: '/*/info/issue-medical-supply/:departmentKey',
        component: lazy(() =>
          import('./views/panels/hospital/IssueMedicalSupplyDashboard/PatientMedicalSupply'),
        ),
      },
      {
        exact: true,
        path: '/*/info/hosp-medical-certificate',
        component: lazy(() =>
          import('./views/panels/hospital/MedicalCertificate'),
        ),
      },
      {
        exact: true,
        path: '/*/ipd/info/admission-note-navy',
        component: lazy(() =>
          import(
            './views/panels/hospital/AdmissionNoteNavy'
          ),
        ),
      },
      {
        exact: true,
        path: '/*/ipd/info/summary-sheet-navy',
        component: lazy(() =>
          import(
            './views/panels/hospital/SummarySheetNavy'
          ),
        ),
      },
      {
        exact: true,
        path: '/*/ipd/info/discharge-summary-navy',
        component: lazy(() =>
          import(
            './views/panels/hospital/DischargeSummaryNavy'
          ),
        ),
      },
      {
        exact: true,
        path: '/*/ipd/info/treatment-consent-navy',
        component: lazy(() =>
          import(
            './views/panels/hospital/TreatmentConsentNavy'
          ),
        ),
      },
      {
        exact: true,
        path: '/*/info/labour-delivery-record',
        component: lazy(() =>
          import('./views/panels/hospital/LabourDeliveryRecord'),
        ),
      },
      {
        exact: true,
        path: '/*/info/form-billing-summary',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/FormBillingSummary'),
        ),
      },
      {
        exact: true,
        path: '/patient-file/surgery/display/form/form-surgery-info',
        component: lazy(() =>
          import(
            './views/panels/hospital/Surgery/SurgeryInfoForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/patient-file/surgery/display/form/form-surgery-operation',
        component: lazy(() =>
          import(
            './views/panels/hospital/Surgery/SurgeryOperationForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/patient-file/surgery/display/form/form-operation',
        component: lazy(() =>
          import(
            './views/panels/hospital/Surgery/SurgeryOperationForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/patient-file/surgery/display/form/form-surgery-anesthetic',
        component: lazy(() =>
          import(
            './views/panels/hospital/Surgery/SurgeryAnestheticForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/patient-file/surgery/display/form/form-anesthetic',
        component: lazy(() =>
          import(
            './views/panels/hospital/Surgery/SurgeryAnestheticForm'
          ),
        ),
      },
      {
        exact: true,
        path:
          '/patient-file/surgery/display/form/form-surgery-service-charge',
        component: lazy(() =>
          import(
            './views/panels/hospital/Surgery/SurgeryServiceChargeForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/*/dashboard/analytics/',
        component: lazy(() => import('./views/patient/DashAnalytics')),
      },
      {
        exact: true,
        path: '/*/ipd/therapies-calendar/',
        component: lazy(() =>
          import('./views/panels/hospital/TherapiesCalendar/index'),
        ),
      },
      {
        exact: true,
        path: '/*/ipd/e-mar/',
        component: lazy(() =>
          import('./views/panels/hospital/Emar/Patient/index'),
        ),
      },
      {
        exact: true,
        path: '/*/info/billing/',
        component: lazy(() => import('./views/panels/hospital/Billing/index')),
      },
      {
        exact: true,
        path: '/*/info/billing/summary/',
        component: lazy(() =>
          import('./views/panels/hospital/Billing/BillingSummary'),
        ),
      },
      {
        exact: true,
        path: '/*/info/prescription/',
        component: lazy(() =>
          import('./views/panels/hospital/Prescription/index'),
        ),
      },
      {
        exact: true,
        path: '/*/info/medication/',
        component: lazy(() =>
          import('./views/panels/hospital/Medication/index'),
        ),
      },
      {
        exact: true,
        path: '/*/info/home-medication/',
        component: lazy(() =>
          import('./views/panels/hospital/HomeMedication/index'),
        ),
      },
      {
        exact: true,
        path: '/*/info/progress-note/:hn/:an',
        component: lazy(() =>
          import('./views/panels/hospital/ProfessionalProgressNote'),
        ),
      },
      {
        exact: true,
        path: '/*/info/progress-note/',
        component: lazy(() =>
          import('./views/panels/hospital/ProfessionalProgressNote'),
        ),
      },
      {
        exact: true,
        path: '/*/info/nutrition/:hn/:an',
        component: lazy(() => import('./views/panels/hospital/Nutrition/PatientNutrition')),
      },
      {
        exact: true,
        path: '/*/info/nutrition',
        component: lazy(() => import('./views/panels/hospital/Nutrition/PatientNutrition')),
      },
      {
        exact: true,
        path: '/*/info/order/:hn/:an',
        component: lazy(() => import('./views/panels/hospital/Order/index')),
      },
      {
        exact: true,
        path: '/*/info/order/',
        component: lazy(() => import('./views/panels/hospital/Order/index')),
      },
      {
        exact: true,
        path: '/*/info/vital-sign/',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/index'),
        ),
      },
      {
        exact: true,
        path: '/*/info/vital-sign-chart/',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/VitalSignChart'),
        ),
      },
      {
        exact: true,
        path: '/*/info/neuro-chart',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/NeuroChart'),
        ),
      },
      {
        exact: true,
        path: '/*/info/neuro-assessment',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/NeuroAssessment'),
        ),
      },
      {
        exact: true,
        path: '/*/info/adult-fall-risk-assessment',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/FallRiskAssessment'),
        ),
      },
      {
        exact: true,
        path: '/*/info/anesthetic-record-chart',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/AnestheticRecord'),
        ),
      },
      {
        exact: true,
        path: '/*/info/anes-record-chart',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/AnesRecordChart'),
        ),
      },
      {
        exact: true,
        path: '/*/info/monitor-chart/',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/MonitorChart'),
        ),
      },
      {
        exact: true,
        path: '/*/info/hosp-module-history/',
        component: lazy(() => import('./components/ModuleHistory/index')),
      },
      {
        exact: true,
        path: '/*/info/hosp-images/',
        component: lazy(() => import('./views/panels/hospital/Images/index')),
      },
      {
        exact: true,
        path: '/*/info/hosp-document/',
        component: lazy(() => import('./components/Document/DocumentViewer')),
      },
      {
        exact: true,
        path: '/*/info/hosp-report/',
        component: lazy(() =>
          import('./components/Report/ReportPatientViewer'),
        ),
      },
      {
        exact: true,
        path: '/*/info/hosp-visit-report/',
        component: lazy(() =>
          import('./components/Report/ReportPatientVisitViewer'),
        ),
      },
      {
        exact: true,
        path: '/*/info/hosp-lab-document/',
        component: lazy(() =>
          import('./views/panels/hospital/Lab/LabReport/LabReportViewer'),
        ),
      },
      {
        exact: true,
        path: '/*/info/radiology-order',
        component: lazy(() =>
          import('./views/panels/hospital/RadiologyOrder'),
        ),
      },
      {
        exact: true,
        path: '/*/info/radiology-order-detail',
        component: lazy(() =>
          import('./views/panels/hospital/RadiologyOrderDetail'),
        ),
      },
      {
        exact: true,
        path: '/*/info/radiology-order/:code?',
        component: lazy(() =>
          import('./views/panels/hospital/RadiologyOrder'),
        ),
      },
      {
        exact: true,
        path: '/*/info/timeline/',
        component: lazy(() => import('./views/panels/hospital/Timeline/index')),
      },
      {
        exact: true,
        path: '/*/info/hosp-genogram/',
        component: lazy(() => import('./views/panels/hospital/Genogram/index')),
      },
      {
        exact: true,
        path: '/*/info/hosp-treatment-history',
        component: lazy(() =>
          import('./views/panels/hospital/PatientEvaluation/TreatmentHistoryRoute'),
        ),
      },
      {
        exact: true,
        path: '/*/info/hosp-patient-evaluation',
        component: lazy(() =>
          import('./views/panels/hospital/PatientEvaluation'),
        ),
      },
      {
        exact: true,
        path: '/*/info/surgery-order',
        component: lazy(() =>
          import('./views/panels/hospital/SurgeryOrder'),
        ),
      },
      {
        exact: true,
        path: '/*/info/dental-chart-exam',
        component: lazy(() =>
          import('./views/panels/hospital/DentalChartExam'),
        ),
      },
      {
        exact: true,
        path: '/*/info/patient-evaluation/',
        component: lazy(() => import('./views/patient/Evaluation/index')),
      },
      {
        exact: true,
        path: '/*/info/patient-eye-exam',
        component: lazy(() =>
          import('./views/panels/hospital/EyeExam'),
        ),
      },
      {
        exact: true,
        path: '/*/info/hosp-invoice/:id',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/Invoice/index'),
        ),
      },
      {
        exact: true,
        path: '/*/info/hosp-dental/',
        component: lazy(() => import('./views/panels/hospital/Dental/index')),
      },
      {
        exact: true,
        path: '/*/info/dental-exam-annual',
        component: lazy(() =>
          import('./views/panels/hospital/DentalAnnualExam'),
        ),
      },
      {
        exact: true,
        path: '/*/info/hosp-laboratory',
        component: lazy(() => import('./views/panels/hospital/Lab')),
      },
      {
        exact: true,
        path: '/*/info/labor-room',
        component: lazy(() =>
          import('./views/panels/hospital/LaborRoom/index'),
        ),
      },
      {
        exact: true,
        path: '/*/info/patient-assessment/',
        component: lazy(() =>
          import('./views/panels/hospital/PatientAssessment/IpdAssessment'),
        ),
      },
      {
        exact: true,
        path: '/*/report/patent-checkup-result',
        component: lazy(() => import('./views/panels/hospital/Checkup')),
      },
      {
        exact: true,
        path: '/*/info/health-checkup-lite',
        component: lazy(() =>
          import('./views/panels/hospital/Checkup/HealthCheckupLite'),
        ),
      },
      {
        exact: true,
        path: '/*/info/dashboard/',
        component: lazy(() =>
          import('./views/panels/hospital/Dashboard/index'),
        ),
      },
      {
        exact: true,
        path: '/*/info/doctor-fee-charge',
        component: lazy(() =>
          import('./views/panels/hospital/DoctorFeeCharge/index'),
        ),
      },
      {
        exact: true,
        path:
          '/*/info/form-ophthalmology',
        component: lazy(() =>
          import('./views/panels/hospital/Ophthalmology'),
        ),
      },
      {
        exact: true,
        path: '/*/display/form/form-dental-record',
        component: lazy(() =>
          import('./views/panels/hospital/DentalRecord'),
        ),
      },
      {
        exact: true,
        path: '/*/display/form/:formCode(form-vital-sign)',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/FormVitalSign'),
        ),
      },
      {
        exact: true,
        path: '/*/display/form/:formCode',
        component: lazy(() => import('./designer/display/form')),
      },
      {
        exact: true,
        path: '/*/display/form/:formCode/:hn',
        component: lazy(() => import('./designer/display/form')),
      },
      {
        exact: true,
        path: '/*/page/page-dtx-chart/:hn',
        component: lazy(() =>
          import(
            './views/panels/hospital/VitalSign/DtxMonitoringChart'
          ),
        ),
      },
      {
        exact: true,
        path: '/*/page/page-dtx-chart',
        component: lazy(() =>
          import(
            './views/panels/hospital/VitalSign/DtxMonitoringChart'
          ),
        ),
      },
      {
        exact: true,
        path: '/*/display/page/page-dtx-chart/:hn',
        component: () => <Redirect to="/404" />,
      },
      {
        exact: true,
        path: '/*/display/page/page-dtx-chart',
        component: () => <Redirect to="/404" />,
      },
      {
        exact: true,
        path: '/*/display/page/focus-note',
        component: lazy(() =>
          import('./views/panels/hospital/Ipd/FocusNote')
        ),
      },
      {
        exact: true,
        path: '/*/display/page/nurse-note',
        component: lazy(() =>
          import('./views/panels/hospital/Ipd/NurseNote')
        ),
      },
      {
        exact: true,
        path: '/*/display/page/:pageCode',
        component: lazy(() => import('./designer/display/page')),
      },
      {
        exact: true,
        path: '/*/display/page/:pageCode/:hn',
        component: lazy(() => import('./designer/display/page')),
      },
      {
        exact: true,
        path: '/*/display/widget/:widgetCode',
        component: lazy(() => import('./designer/display/widget')),
      },
      {
        exact: true,
        path: '/*/display/dashboard/:dashboardCode',
        component: lazy(() => import('./designer/display/dashboard')),
      },
      {
        exact: true,
        path: '/*/display/favorite/:favoriteCode',
        component: lazy(() => import('./designer/display/favorite')),
      },
      {
        exact: true,
        path: '/*/consult/request',
        component: lazy(() =>
          import('./views/panels/hospital/Consult/ConsultList'),
        ),
      },
      {
        exact: true,
        path: '/*/report/treatment-plan',
        component: lazy(() => import('./views/panels/hospital/TreatmentPlan')),
      },
      {
        exact: true,
        path: '/*/report/report-clinical-summary',
        component: lazy(() =>
          import('./views/panels/hospital/ClinicalSummary'),
        ),
      },
      {
        exact: true,
        path: '/*/report/report-dc-summary',
        component: lazy(() => import('./views/panels/hospital/DCSummary')),
      },
      {
        exact: true,
        path: '/*/drg/drg-calculator',
        component: lazy(() =>
          import('./views/panels/hospital/Drg/DrgCalculator'),
        ),
      },
      {
        exact: true,
        path: '/*/book-appointment/month',
        component: lazy(() =>
          import('./views/panels/hospital/Appointment/calendar/index'),
        ),
      },
      {
        path: '/*/*',
        exact: true,
        component: lazy(() => import('./views/panels/support/WelcomePage')),
      },
    ],
  },
  {
    path: '/pcu-file',
    layout: AdminLayout,
    guard: AuthGuard,
    routes: [
      {
        exact: true,
        path: '/*/display/form/:formCode',
        component: lazy(() => import('./designer/display/form')),
      },
      {
        exact: true,
        path: '/*/display/form/:formCode/:hn',
        component: lazy(() => import('./designer/display/form')),
      },
      {
        exact: true,
        path: '/*/display/page/:pageCode',
        component: lazy(() => import('./designer/display/page')),
      },
      {
        exact: true,
        path: '/*/display/page/:pageCode/:hn',
        component: lazy(() => import('./designer/display/page')),
      },
      {
        exact: true,
        path: '/*/display/widget/:widgetCode',
        component: lazy(() => import('./designer/display/widget')),
      },
      {
        exact: true,
        path: '/*/display/dashboard/:dashboardCode',
        component: lazy(() => import('./designer/display/dashboard')),
      },
      {
        exact: true,
        path: '/*/display/favorite/:favoriteCode',
        component: lazy(() => import('./designer/display/favorite')),
      },
      {
        path: '/*/*',
        exact: true,
        component: lazy(() => import('./views/panels/support/WelcomePage')),
      },
    ],
  },
  {
    path: '/super-admin',
    layout: AdminLayout,
    guard: AuthGuard,
    routes: [
      {
        exact: true,
        path: '/*/display/form/:formCode',
        component: lazy(() => import('./designer/display/form')),
      },
      {
        exact: true,
        path: '/*/display/page/:pageCode',
        component: lazy(() => import('./designer/display/page')),
      },
      {
        exact: true,
        path: '/*/hospital/export-design',
        component: lazy(() =>
          import('./views/panels/hospital/Export-dashboard/index'),
        ),
      },
      {
        exact: true,
        path: '/*/hospital/export-design/new',
        component: lazy(() =>
          import('./views/panels/hospital/Exportdesign/index'),
        ),
      },
      {
        exact: true,
        path: '/*/hospital/export-design/:id',
        component: lazy(() =>
          import('./views/panels/hospital/Exportdesign/index'),
        ),
      },
      {
        exact: false,
        path: '/*/test/debugnotification',
        component: lazy(() => import('./views/panels/test/DebugNotification')),
      },
      {
        exact: false,
        path: '/*/test/debug-alert-component',
        component: lazy(() =>
          import('./views/panels/test/DebugAlertSubscribe'),
        ),
      },
      {
        exact: false,
        path: '/*/test/audio-analyzer',
        component: lazy(() =>
          import('./views/panels/test/AudioAnalyzerTest'),
        ),
      },
      {
        exact: true,
        path: '/*/bi/dashboard',
        component: lazy(() => import('./views/panels/hospital/BiDashboard')),
      },
      {
        exact: true,
        path: '/*/bi/metabase-chart',
        component: lazy(() => import('./views/panels/bi/MetabaseChart')),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-dashboard',
        component: lazy(() => import('./views/panels/hospital/BiDashboard')),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-dashboard-viewer',
        component: lazy(() =>
          import('./views/panels/hospital/BiDashboardViewer'),
        ),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-report',
        component: lazy(() => import('./views/panels/hospital/Report/index')),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-report/:id',
        component: lazy(() => import('./views/panels/hospital/Report/AddForm')),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-report/previewExample/:code',
        component: lazy(() =>
          import('./views/panels/hospital/Report/preview/index'),
        ),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-report/preview/:id',
        component: lazy(() =>
          import('./views/panels/hospital/Report/preview/index'),
        ),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-report-classic',
        component: lazy(() => import('./views/panels/hospital/ClassicReport')),
      },
      {
        // a bench for the desktop printing chain, deliberately not wired into
        // any workflow so trying it cannot disturb anyone's printing.
        exact: true,
        path: '/*/hospital/hosp-desktop-print-test',
        component: lazy(() =>
          import('./views/panels/hospital/ClassicReport/PrintReportDesktop'),
        ),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-policy-config',
        component: lazy(() =>
          import('./views/panels/setting/PolicyConfig/index'),
        ),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-global-config',
        component: lazy(() =>
          import('./views/panels/setting/GlobalConfig/index'),
        ),
      },
      {
        exact: true,
        path: '/*/hospital/hosp-report-classic/:id/revision/:revisionId',
        component: lazy(() =>
          import('./views/panels/hospital/ClassicReport/CreateReport'),
        ),
      },
      {
        path: '/*/hospital/hosp-report-classic/:id',
        component: lazy(() =>
          import('./views/panels/hospital/ClassicReport/CreateReport'),
        ),
      },
      {
        exact: true,
        path: '/*/hospital/form-assessment',
        component: lazy(() => import('./designer/menu/assessment')),
      },
      {
        exact: true,
        guard: GuestGuard,
        path: '/*/hospital/form-assessment/:id/preview',
        component: lazy(() => import('./designer/menu/assessment/Preview')),
      },
      {
        exact: true,
        path: '/*/hospital/form-assessment/add',
        component: lazy(() => import('./designer/menu/assessment/AddForm')),
      },
      {
        exact: true,
        path: '/*/hospital/form-assessment/:id',
        component: lazy(() => import('./designer/menu/assessment/AddForm')),
      },
      {
        exact: true,
        path: '/*/hospital/lab-order-form-designer',
        component: lazy(() =>
          import('./views/panels/setting/LabFormDesigner'),
        ),
      },
      {
        path: '/*/api-designer',
        component: lazy(() =>
          import('./views/panels/admin-panel/api-designer'),
        ),
      },
      {
        path: '/*/device-gateway/hl7-routes',
        component: lazy(() =>
          import('./views/panels/admin-panel/DeviceGateway'),
        ),
      },
      {
        path: '/*/device-gateway',
        component: lazy(() =>
          import('./views/panels/admin-panel/DeviceGateway'),
        ),
      },
      {
        path: '/*/hospital-layout',
        component: lazy(() =>
          import('./views/panels/admin-panel/HospitalLayout'),
        ),
      },
      {
        path: '/*/group-management',
        component: lazy(() =>
          import('./views/panels/admin-panel/AccessManagement/GroupManagement'),
        ),
      },
      {
        path: '/*/org-theme',
        component: lazy(() => import('./views/panels/admin-panel/OrgTheme')),
      },
      {
        path: '/*/folder-management',
        component: lazy(() =>
          import('./views/panels/admin-panel/FolderManagement'),
        ),
      },
      {
        path: '/*/backup-management',
        component: lazy(() =>
          import('./views/panels/admin-panel/BackupManagement'),
        ),
      },
      {
        path: '/*/debug-iv',
        component: lazy(() =>
          import('./views/panels/hospital/Order/IVFluidTable'),
        ),
      },
    ],
  },
  {
    path: '*',
    layout: AdminLayout,
    guard: AuthGuard,
    routes: [
      {
        path: ['page-search-hn', 'page-rpt-search-hn', 'page-search-patient'],
        component: lazy(() =>
          import('./views/panels/hospital/PatientRegister/components/DesignerPatientSearchModal'),
        ),
      },
      {
        path: ['page-rpt-search-vn', 'search-pas-patient-vn'],
        component: lazy(() =>
          import('./views/panels/hospital/PatientRegister/components/DesignerPatientVisitSearchModal'),
        ),
      },
      {
        path: ['page-rpt-search-an', 'search-pas-an'],
        component: lazy(() =>
          import('./views/panels/hospital/PatientRegister/components/DesignerPatientAdmissionSearchModal'),
        ),
      },
      {
        exact: true,
        path: '/work/',
        component: () => <Redirect to="/work/hub" />,
      },
      {
        exact: true,
        path: '/work/hub',
        component: lazy(() => import('./views/work/hub/WorkHub')),
      },
      {
        exact: true,
        path: '/work/trello',
        component: lazy(() => import('./views/work/trello/WorkTrello')),
      },
      {
        exact: true,
        path: '/work/line-bot',
        component: lazy(() => import('./views/work/line-bot/LineBot')),
      },
      {
        exact: true,
        path: '/work/notification',
        component: lazy(() => import('./views/work/notification/Notification')),
      },
      {
        exact: true,
        path: '/work/dashboard',
        component: lazy(() => import('./views/work/dashboard/WorkDashboard')),
      },
      {
        exact: true,
        path: '/work/setting',
        component: lazy(() => import('./views/work/settings/WorkSettings')),
      },
      {
        exact: true,
        path: '/app/dashboard/analytics',
        component: lazy(() => import('./views/dashboard/DashAnalytics')),
      },
      {
        exact: true,
        path: '/app/dashboard/sales',
        component: lazy(() => import('./views/dashboard/DashSales')),
      },
      {
        exact: true,
        path: '/app/widgets/widget-statistic',
        component: lazy(() => import('./views/widgets/WidgetStatistic')),
      },
      {
        exact: true,
        path: '/app/widgets/widget-data',
        component: lazy(() => import('./views/widgets/WidgetData')),
      },
      {
        exact: true,
        path: '/app/widgets/widget-chart',
        component: lazy(() => import('./views/widgets/WidgetChart')),
      },
      {
        exact: true,
        path: '/layout/vertical/static',
        component: lazy(() => import('./views/page-layouts/vertical/Static')),
      },
      {
        exact: true,
        path: '/layout/vertical/fixed',
        component: lazy(() => import('./views/page-layouts/vertical/Fixed')),
      },
      {
        exact: true,
        path: '/layout/vertical/nav-fixed',
        component: lazy(() => import('./views/page-layouts/vertical/NavFixed')),
      },
      {
        exact: true,
        path: '/layout/vertical/collapse-menu',
        component: lazy(() =>
          import('./views/page-layouts/vertical/CollapseMenu'),
        ),
      },
      {
        exact: true,
        path: '/layout/vertical/v-rtl',
        component: lazy(() =>
          import('./views/page-layouts/vertical/RTLLayout'),
        ),
      },
      {
        exact: true,
        path: '/layout/horizontal',
        component: lazy(() => import('./views/page-layouts/horizontal/Static')),
      },
      {
        exact: true,
        path: '/layout/horizontal-v2',
        component: lazy(() =>
          import('./views/page-layouts/horizontal/Static-v2'),
        ),
      },
      {
        exact: true,
        path: '/layout/horizontal-rtl',
        component: lazy(() =>
          import('./views/page-layouts/horizontal/RTLLayout'),
        ),
      },
      {
        exact: true,
        path: '/layout/box',
        component: lazy(() => import('./views/page-layouts/extra-layouts/Box')),
      },
      {
        exact: true,
        path: '/layout/light',
        component: lazy(() =>
          import('./views/page-layouts/extra-layouts/Light'),
        ),
      },
      {
        exact: true,
        path: '/layout/dark',
        component: lazy(() =>
          import('./views/page-layouts/extra-layouts/Dark'),
        ),
      },
      {
        exact: true,
        path: '/users/user-profile',
        component: lazy(() => import('./views/users/userprofile')),
      },
      {
        exact: true,
        path: '/users/news-feed',
        component: lazy(() => import('./views/users/newsfeed')),
      },
      {
        exact: true,
        path: '/users/user-cards',
        component: lazy(() => import('./views/users/UserCard')),
      },
      {
        exact: true,
        path: '/users/borrow-file',
        component: lazy(() => import('./views/users/BorrowFile')),
      },
      {
        exact: true,
        path: '/users/user-list',
        component: lazy(() => import('./views/users/UserList')),
      },
      {
        exact: true,
        path: '/users/user-theme',
        component: lazy(() => import('./views/users/UserTheme')),
      },
      {
        exact: true,
        path: '/users/full-calendar',
        component: lazy(() =>
          import('./views/users/calendar/FullEventCalendar'),
        ),
      },
      {
        exact: true,
        path: '/users/notification',
        component: lazy(() =>
          import('./views/users/notification/NotificationList'),
        ),
      },
      {
        exact: true,
        path: '/basic/alert',
        component: lazy(() => import('./views/ui-elements/basic/BasicAlert')),
      },
      {
        exact: true,
        path: '/basic/button',
        component: lazy(() => import('./views/ui-elements/basic/BasicButton')),
      },
      {
        exact: true,
        path: '/basic/badges',
        component: lazy(() => import('./views/ui-elements/basic/BasicBadges')),
      },
      {
        exact: true,
        path: '/basic/breadcrumb-pagination',
        component: lazy(() =>
          import('./views/ui-elements/basic/BasicBreadcrumbPagination'),
        ),
      },
      {
        exact: true,
        path: '/basic/cards',
        component: lazy(() => import('./views/ui-elements/basic/BasicCards')),
      },
      {
        exact: true,
        path: '/basic/collapse',
        component: lazy(() =>
          import('./views/ui-elements/basic/BasicCollapse'),
        ),
      },
      {
        exact: true,
        path: '/basic/carousel',
        component: lazy(() =>
          import('./views/ui-elements/basic/BasicCarousels'),
        ),
      },
      {
        exact: true,
        path: '/basic/grid-system',
        component: lazy(() =>
          import('./views/ui-elements/basic/BasicGridSystem'),
        ),
      },
      {
        exact: true,
        path: '/basic/progress',
        component: lazy(() =>
          import('./views/ui-elements/basic/BasicProgress'),
        ),
      },
      {
        exact: true,
        path: '/basic/modal',
        component: lazy(() => import('./views/ui-elements/basic/BasicModals')),
      },
      {
        exact: true,
        path: '/basic/spinner',
        component: lazy(() => import('./views/ui-elements/basic/BasicSpinner')),
      },
      {
        exact: true,
        path: '/basic/tabs-pills',
        component: lazy(() =>
          import('./views/ui-elements/basic/BasicTabsPills'),
        ),
      },
      {
        exact: true,
        path: '/basic/typography',
        component: lazy(() =>
          import('./views/ui-elements/basic/BasicTypography'),
        ),
      },
      {
        exact: true,
        path: '/basic/tooltip-popovers',
        component: lazy(() =>
          import('./views/ui-elements/basic/BasicTooltipsPopovers'),
        ),
      },
      {
        exact: true,
        path: '/basic/other',
        component: lazy(() => import('./views/ui-elements/basic/BasicOther')),
      },
      {
        exact: true,
        path: '/hospital/refer/refer-dashboard',
        component: lazy(() => import('./views/panels/hospital/Refer')),
      },
      {
        exact: true,
        path: '/hospital/hosp-dashboard',
        component: lazy(() => import('./views/panels/hospital/Dashboard')),
      },
      {
        // The same bench as the one registered under the perspective parent
        // above: a link is pasted to reach this page rather than clicked in a
        // menu, and a pasted link often has no perspective segment. Every
        // working /hospital/... page is registered in both places for the same
        // reason — see hosp-dashboard, directly above.
        exact: true,
        path: '/hospital/hosp-desktop-print-test',
        component: lazy(() =>
          import('./views/panels/hospital/ClassicReport/PrintReportDesktop'),
        ),
      },
      {
        exact: true,
        path: '/hospital/goods-requisition',
        component: lazy(() =>
          import('./views/panels/hospital/GoodsRequisitionDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/goods-requisition/manage',
        component: lazy(() =>
          import(
            './views/panels/hospital/GoodsRequisitionDashboard/GoodsRequisitionForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/hospital/goods-requisition/manage/:goodsIssueId',
        component: lazy(() =>
          import(
            './views/panels/hospital/GoodsRequisitionDashboard/GoodsRequisitionForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/bi/dashboard',
        component: lazy(() => import('./views/panels/hospital/BiDashboard')),
      },
      {
        exact: true,
        path: '/bi/metabase-chart',
        component: lazy(() => import('./views/panels/bi/MetabaseChart')),
      },
      {
        exact: true,
        path: '/hospital/clinical/opd-dental-room',
        component: lazy(() => import('./views/panels/hospital/DentalRoom').then((module) => ({ default: module.OpdDentalRoom }))),
      },
      {
        exact: true,
        path: '/hospital/clinical/ipd-dental-room',
        component: lazy(() => import('./views/panels/hospital/DentalRoom').then((module) => ({ default: module.IpdDentalRoom }))),
      },
      {
        exact: true,
        path: '/hospital/clinical/doctor-room',
        component: lazy(() =>
          import('./views/panels/hospital/DoctorRoom'),
        ),
      },
      {
        exact: true,
        path: '/hospital/epidem-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/EpidemDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/lab-request-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/LabRequestDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/financial-activity',
        component: lazy(() =>
          import('./views/panels/hospital/FinancialActivity'),
        ),
      },
      {
        exact: true,
        path: '/hospital/nutrition/dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/Nutrition/Dashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/nutrition/daily-meal',
        component: lazy(() =>
          import('./views/panels/hospital/Nutrition/DailyMeal'),
        ),
      },
      {
        exact: true,
        path: '/hospital/nutrition/patient-assessment',
        component: lazy(() =>
          import('./views/panels/hospital/Nutrition/PatientAssessment'),
        ),
      },
      {
        exact: true,
        path: '/hospital/clinical/clinic-room',
        component: lazy(() =>
          import('./views/panels/hospital/ClinicDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/opd-stage-report',
        component: lazy(() =>
          import('./views/panels/hospital/PatientJourneyStageReport'),
        ),
      },
      {
        exact: true,
        path: '/hospital/surgery-waiting-suggestion-report',
        component: lazy(() =>
          import('./views/panels/hospital/SurgeryWaitingSuggestionReport'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-surgery',
        component: lazy(() =>
          import('./views/panels/hospital/Surgery'),
        ),
      },
      {
        exact: true,
        path: '/hr/hosp-department',
        component: lazy(() => import('./views/panels/hospital/Department')),
      },
      {
        exact: true,
        path: '/hr/hosp-doctor',
        component: lazy(() => import('./views/panels/hospital/Doctor')),
      },
      {
        exact: true,
        path: '/hospital/hosp-patient',
        component: lazy(() => import('./views/panels/hospital/Patient')),
      },
      {
        exact: true,
        path: '/hospital/patient-register',
        component: lazy(() =>
          import('./views/panels/hospital/PatientRegister'),
        ),
      },
      {
        exact: true,
        path: '/hospital/check-in',
        component: lazy(() =>
          import('./views/panels/hospital/HospCheckInDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-check-in',
        component: lazy(() => import('./views/panels/hospital/HospCheckIn')),
      },
      {
        exact: true,
        path: '/hr/hosp-nurse',
        component: lazy(() => import('./views/panels/hospital/Nurse')),
      },
      {
        exact: true,
        path: '/hr/hosp-pharmacist',
        component: lazy(() => import('./views/panels/hospital/Pharmacist')),
      },
      {
        exact: true,
        path: '/hr/payroll',
        component: lazy(() => import('./views/panels/hr/Payroll')),
      },
      {
        exact: true,
        path: '/hr/doctor-fee-dashboard',
        component: lazy(() =>
          import('./views/panels/hr/DoctorFeeDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hr/staff-dashboard',
        component: lazy(() =>
          import('./views/panels/hr/StaffDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hr/payroll-item-config',
        component: lazy(() =>
          import('./views/panels/hr/PayrollItemConfig'),
        ),
      },
      {
        exact: true,
        path: '/hr/payroll-setting',
        component: lazy(() => import('./views/panels/hr/PayrollSetting')),
      },
      {
        exact: true,
        path: '/hr/salary',
        component: lazy(() => import('./views/panels/hr/EmployeeSalary')),
      },
      {
        exact: true,
        path: '/hr/salary-create',
        component: lazy(() => import('./views/panels/hr/SalaryCreate')),
      },
      {
        exact: true,
        path: '/hr/shift-roster',
        component: lazy(() => import('./views/panels/hr/ShiftRoster')),
      },
      {
        exact: true,
        path: '/hr/attendance-management',
        component: lazy(() => import('./views/panels/hr/AttendanceManagement')),
      },
      {
        exact: true,
        path: '/hr/employee-attendance',
        component: lazy(() =>
          import('./views/panels/hr/EmployeeAttendanceImport'),
        ),
      },
      {
        exact: true,
        path: '/employee/personal/:en',
        component: lazy(() => import('./views/users/userprofile')),
      },
      {
        exact: true,
        path: '/employee/job/:en',
        component: lazy(() => import('./views/users/userprofile')),
      },
      {
        exact: true,
        path: '/hospital/hosp-laboratory',
        component: lazy(() => import('./views/panels/hospital/Lab')),
      },
      {
        exact: true,
        path: '/hospital/hosp-lab-result',
        component: lazy(() =>
          import('./views/panels/hospital/Lab/ResultDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-lab-case',
        component: lazy(() =>
          import('./views/panels/hospital/Lab/CaseDashboard'),
        ),
      },
      {
        exact: true,
        path: '/blood-bank/blood-return',
        component: lazy(() =>
          import('./views/panels/hospital/BloodReturn'),
        ),
      },
      {
        exact: true,
        path: '/blood-bank/blood-issue',
        component: lazy(() =>
          import('./views/panels/hospital/BloodIssue'),
        ),
      },
      {
        exact: true,
        path: '/blood-bank/product-preparation',
        component: lazy(() =>
          import('./views/panels/hospital/BloodProductPreparation'),
        ),
      },
      {
        exact: true,
        path: '/blood-bank/master-data',
        component: lazy(() =>
          import('./views/panels/hospital/BloodBankMasterData'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-appointment',
        component: lazy(() =>
          import('./views/panels/hospital/Appointment/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/appointment-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/Appointment/dashboard'),
        ),
      },
      {
        exact: true,
        path: [
          '/hospital/hosp-appointment-calendar/month',
          '/hospital/hosp-appointment-calendar',
        ],
        component: lazy(() =>
          import('./views/panels/hospital/Appointment/calendar/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/appointment-calendar',
        component: lazy(() =>
          import('./views/panels/hospital/Appointment/calendar/Calendar'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-room-schedule',
        component: lazy(() =>
          import('./views/panels/hospital/Appointment/calendar/RoomSchedule'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-doctor-schedule',
        component: lazy(() =>
          import('./views/panels/hospital/Appointment/calendar/DoctorSchedule'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-surgery-schedule',
        component: lazy(() =>
          import(
            './views/panels/hospital/Appointment/calendar/SurgerySchedule'
          ),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-operation-schedule',
        component: lazy(() =>
          import(
            './views/panels/hospital/Appointment/calendar/OperationSchedule'
          ),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-ward',
        component: lazy(() => import('./views/panels/hospital/Ward/index')),
      },
      {
        exact: true,
        path: '/hospital/hosp-bed',
        component: lazy(() => import('./views/panels/hospital/Bed/index')),
      },
      {
        exact: true,
        path: '/hospital/hosp-borrow',
        component: lazy(() =>
          import('./views/panels/hospital/BorrowFile/Dashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-queue-dashboard',
        component: lazy(() => import('./components/Queue/Dashboard')),
      },
      {
        exact: true,
        path: '/hospital/hosp-queue-discharge',
        component: lazy(() => import('./components/Queue/Discharge')),
      },
      {
        exact: true,
        path: '/hospital/hosp-queue-call',
        component: lazy(() => import('./components/Queue/Call')),
      },
      {
        exact: true,
        path: '/hospital/issue-medical-supply-dashboard/:departmentKey',
        component: lazy(() =>
          import('./views/panels/hospital/IssueMedicalSupplyDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-pharmacy-medical-supply',
        component: lazy(() =>
          import('./views/panels/hospital/PharmacyMedicalSupply'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-pharmacy',
        component: lazy(() => import('./views/panels/hospital/Pharmacy/index')),
      },
      {
        exact: true,
        path: '/hospital/pharmacy/drug-cross-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/Pharmacy/DrugCrossDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-opd-order-med',
        component: lazy(() =>
          import('./views/panels/hospital/Pharmacy/OpdMedication'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-opd-manage-plan',
        component: lazy(() =>
          import('./views/panels/hospital/Opd/ManagementPlan'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-review-med',
        component: lazy(() =>
          import('./views/panels/hospital/Pharmacy/ReviewMed'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-restore',
        component: lazy(() =>
          import('./views/panels/hospital/Pharmacy/Restore/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-med-section/:id',
        component: lazy(() =>
          import('./views/panels/hospital/Pharmacy/MedSection'),
        ),
      },
      {
        exact: true,
        path: '/hospital/social-work-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/SocialWork/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-genogram',
        component: lazy(() => import('./views/panels/hospital/Genogram/index')),
      },
      {
        exact: true,
        path: '/hospital/hosp-finance',
        component: lazy(() => import('./views/panels/hospital/Finance/index')),
      },
      {
        exact: true,
        path: '/hospital/finance/cash-management',
        component: lazy(() => import('./views/panels/hospital/Finance/CashManagement/Dashboard')),
      },
      {
        exact: true,
        path: '/hospital/finance/cash-management/drawers',
        component: lazy(() => import('./views/panels/hospital/Finance/CashManagement/Drawers')),
      },
      {
        exact: true,
        path: '/hospital/finance/cash-management/shifts',
        component: lazy(() => import('./views/panels/hospital/Finance/CashManagement/Shifts')),
      },
      {
        exact: true,
        path: '/hospital/finance/cash-management/shifts/:shiftId',
        component: lazy(() => import('./views/panels/hospital/Finance/CashManagement/ShiftDetail')),
      },
      {
        exact: true,
        path: '/hospital/finance/cash-management/transactions',
        component: lazy(() => import('./views/panels/hospital/Finance/CashManagement/Transactions')),
      },
      {
        exact: true,
        path: '/hospital/finance/cash-management/transfers',
        component: lazy(() => import('./views/panels/hospital/Finance/CashManagement/Transfers')),
      },
      {
        exact: true,
        path: '/hospital/finance/cash-management/transfers/:transferId',
        component: lazy(() => import('./views/panels/hospital/Finance/CashManagement/TransferDetail')),
      },
      {
        exact: true,
        path: '/hospital/finance/cash-management/reports',
        component: lazy(() => import('./views/panels/hospital/Finance/CashManagement/Reports')),
      },
      {
        exact: true,
        path: '/hospital/finance/finance-opd-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/FinanceOpdDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/finance/finance-debt-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/FinanceDebtDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/finance/finance-ipd-queue',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/FinanceIpdQueue'),
        ),
      },
      {
        exact: true,
        path: '/hospital/finance/debtor-eclaim-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/DebtorEclaimDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/nhso-check-benefit',
        component: lazy(() =>
          import('./views/panels/hospital/NhsoCheckBenefit'),
        ),
      },
      {
        exact: true,
        path: '/hospital/nhso-close-benefit',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/NhsoCloseBenefit'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-invoice/:id',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/Invoice/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-split-billing',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/SplitBilling'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-dental',
        component: lazy(() => import('./views/panels/hospital/Dental/index')),
      },
      {
        exact: true,
        path: '/hospital/hosp-emar',
        component: lazy(() =>
          import('./views/panels/hospital/Emar/Dashboard/Timeline'),
        ),
      },
      {
        exact: true,
        path: '/hospital/re-diag',
        component: lazy(() =>
          import('./views/panels/hospital/AdmissionNote/ReDiag'),
        ),
      },
      {
        exact: true,
        path: '/hospital/scan-smart-card',
        component: lazy(() => import('./components/SmartCard/ScanSmartCard')),
      },
      {
        exact: true,
        path: '/hospital/kios-smart-card',
        component: lazy(() =>
          import('./views/panels/hospital/Kios/KiosSmartCard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/ipd-pre-admit',
        component: lazy(() =>
          import('./views/panels/hospital/Ipd/PreAdmitDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/medical-ipd-dashboard',
        component: lazy(() =>
          import(
            './views/panels/hospital/MedicalRecord/MedicalIpdDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/hospital/ipd-dc',
        component: lazy(() =>
          import('./views/panels/hospital/Ipd/DcRecordDashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/ipd-pre-admit/detail',
        component: lazy(() =>
          import('./views/panels/hospital/Ipd/PreAdmitDashboard'),
        ),
      },
      {
        exact: true,
        path: '/settings/doctor-fee',
        component: lazy(() => import('./views/panels/setting/DoctorFee/index')),
      },
      {
        exact: true,
        path: '/settings/lab-designer',
        component: lazy(() => import('./views/panels/setting/Lab/LabDesigner')),
      },
      {
        exact: true,
        path: '/settings/lab-order-form-designer',
        component: lazy(() =>
          import('./views/panels/setting/LabFormDesigner'),
        ),
      },
      {
        exact: true,
        path: '/settings/doctor-calendar',
        component: lazy(() =>
          import('./views/panels/setting/DoctorCalendar/index'),
        ),
      },
      {
        exact: true,
        path: '/settings/room-calendar',
        component: lazy(() =>
          import('./views/panels/setting/RoomCalendar/index'),
        ),
      },
      {
        exact: true,
        path: '/settings/surgery-room-calendar',
        component: lazy(() =>
          import('./views/panels/setting/SurgeryRoomCalendar/index'),
        ),
      },
      {
        exact: true,
        path: '/settings/permission-and-role',
        component: lazy(() => import('./views/panels/permission-role/index')),
      },

      {
        exact: true,
        path: '/basicdata/specialty-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/SpecialtyDashboard'),
        ),
      },
      {
        exact: true,
        path: '/basicdata/place-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/PlaceDashboard'),
        ),
      },
      {
        exact: true,
        path: '/basicdata/ward-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/WardDashboard'),
        ),
      },
      {
        exact: true,
        path: '/basicdata/sponsor-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/SponsorDashboard'),
        ),
      },
      {
        exact: true,
        path: '/basicdata/hospital/hospital-item',
        component: lazy(() =>
          import('./views/panels/hospital/HospitalManagement'),
        ),
      },
      {
        exact: true,
        path: '/basicdata/hospital/bed-manager',
        component: lazy(() => import('./views/panels/hospital/Bed/BedManager')),
      },
      {
        exact: true,
        path: '/basicdata/hospital/med-item',
        component: lazy(() => import('./views/panels/hospital/MedItem')),
      },
      {
        exact: true,
        path: '/basicdata/hospital/charge-item',
        component: lazy(() => import('./views/panels/hospital/ChargeItem')),
      },
      {
        exact: true,
        path: '/basicdata/hospital/charge-item-readonly',
        component: lazy(() => import('./views/panels/hospital/ChargeItemReadOnly')),
      },
      {
        exact: true,
        path: '/basicdata/hospital/core-serial',
        component: lazy(() =>
          import('./views/panels/hospital/CoreSerial'),
        ),
      },
      {
        exact: true,
        path: '/basicdata/icd9',
        component: lazy(() => import('./views/panels/hospital/Icd9')),
      },
      {
        exact: true,
        path: '/basicdata/icd10',
        component: lazy(() => import('./views/panels/hospital/Icd10')),
      },
      {
        exact: true,
        path: '/basicdata/operative-item',
        component: lazy(() => import('./views/panels/hospital/OperativeItem')),
      },
      {
        exact: true,
        path: '/basicdata/dental-service-item',
        component: lazy(() =>
          import('./views/panels/hospital/DentalServiceItem'),
        ),
      },
      {
        exact: true,
        path: '/basicdata/laboratory-item',
        component: lazy(() => import('./views/panels/hospital/LaboratoryItem')),
      },
      {
        exact: true,
        path: '/basicdata/hospital/benefit-type-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/BenefitTypeDashboard'),
        ),
      },
      {
        exact: true,
        path: '/basicdata/radioloy-item',
        component: lazy(() =>
          import('./views/panels/hospital/RadiologyItem'),
        ),
      },
      {
        exact: true,
        path: '/basicdata/radiology-item',
        component: lazy(() =>
          import('./views/panels/hospital/RadiologyItem'),
        ),
      },

      {
        exact: true,
        path: '/helpdesk/hd-dashboard',
        component: lazy(() => import('./views/panels/helpdesk/Dashboard')),
      },
      {
        exact: true,
        path: '/hospital/dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/Dashboard/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-report',
        component: lazy(() => import('./views/panels/hospital/Report/index')),
      },
      {
        exact: true,
        path: '/hospital/export-data',
        component: lazy(() =>
          import('./views/panels/hospital/ExportDataDashboard/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/export-data/detail/:id',
        component: lazy(() =>
          import('./views/panels/hospital/ExportDatadetail/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-dashboard-viewer',
        component: lazy(() =>
          import('./views/panels/hospital/BiDashboardViewer'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-report-viewer',
        component: lazy(() =>
          import('./components/Report/ReportHospitalViewer'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-report/:id',
        component: lazy(() => import('./views/panels/hospital/Report/AddForm')),
      },
      {
        exact: true,
        path: '/hospital/hosp-report/previewExample/:code',
        component: lazy(() =>
          import('./views/panels/hospital/Report/preview/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/hosp-report/preview/:id',
        component: lazy(() =>
          import('./views/panels/hospital/Report/preview/index'),
        ),
      },
      {
        exact: true,
        path: '/hospital/telemed/dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/Telemed/Dashboard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/telemed/call',
        component: lazy(() => import('./views/panels/hospital/Telemed/Call')),
      },
      {
        exact: true,
        path: '/hospital/lab-manual',
        component: lazy(() => import('./views/panels/setting/Lab/LabManual')),
      },
      {
        exact: true,
        path: '/stock/medical',
        component: lazy(() => import('./views/panels/stock/Medical/index')),
      },
      {
        exact: true,
        path: '/stock/medical/form',
        component: lazy(() =>
          import('./views/panels/stock/Medical/MedicalForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/medical/form/:productId/:warehouseId',
        component: lazy(() =>
          import('./views/panels/stock/Medical/MedicalForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/company',
        component: lazy(() => import('./views/panels/stock/Company/index')),
      },
      {
        exact: true,
        path: '/stock/company/form',
        component: lazy(() =>
          import('./views/panels/stock/Company/CompanyForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/company/form/:supplierId',
        component: lazy(() =>
          import('./views/panels/stock/Company/CompanyForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/purchase-requisition',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseRequisition/index'),
        ),
      },
      {
        exact: true,
        path: '/stock/purchase-requisition/form',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseRequisition/RequisitionForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/purchase-requisition/form/:purchaseRequisitionId',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseRequisition/RequisitionForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/purchase-order',
        component: lazy(() =>
          import('./views/panels/stock/Accounting/PurchaseOrder/index'),
        ),
      },
      {
        exact: true,
        path: '/stock/purchase-order/form',
        component: lazy(() =>
          import('./views/panels/stock/Accounting/PurchaseOrder/OrderForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/order-receipt',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseReceipt/index'),
        ),
      },
      {
        exact: true,
        path: '/stock/purchase-order/form/:purchaseOrderId',
        component: lazy(() =>
          import('./views/panels/stock/Accounting/PurchaseOrder/OrderForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/order-receipt',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseReceipt/index'),
        ),
      },
      {
        exact: true,
        path: '/stock/order-receipt/form',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseReceipt/ReceiptForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/order-receipt/form/:orderReceiptId',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseReceipt/ReceiptForm'),
        ),
      },
      {
        exact: true,
        path: '/stock/order-receipt/form-non-odrder',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseReceipt/ReceiptFormNonOrder'),
        ),
      },
      {
        exact: true,
        path: '/stock/goods-issue',
        component: lazy(() =>
          import('./views/panels/stock/GoodsIssue/Dashboard'),
        ),
      },

      {
        exact: true,
        path: '/stock/goods-issue/manage',
        component: lazy(() => import('./views/panels/stock/GoodsIssue/Manage')),
      },
      {
        exact: true,
        path: '/stock/goods-issue/manage/:goodsIssueId',
        component: lazy(() => import('./views/panels/stock/GoodsIssue/Manage')),
      },
      {
        exact: true,
        path: '/stock/inventory-management',
        component: lazy(() =>
          import('./views/panels/stock/InventoryManagement'),
        ),
      },
      {
        exact: true,
        path: '/stock/inventory-management/detail',
        component: lazy(() =>
          import('./views/panels/stock/InventoryManagement/Detail'),
        ),
      },
      {
        exact: true,
        path: '/stock/inventory-management/detail/:productId/:warehouseId',
        component: lazy(() =>
          import('./views/panels/stock/InventoryManagement/Detail'),
        ),
      },
      {
        exact: true,
        path:
          '/stock/inventory-management/detail/:productId/:warehouseId/reconcile-inventory/:inventoryLotId',
        component: lazy(() =>
          import('./views/panels/stock/InventoryManagement/ReconcileInventory'),
        ),
      },
      {
        exact: true,
        path: '/stock/stock-taking',
        component: lazy(() => import('./views/panels/stock/StockTaking/index')),
      },
      {
        exact: true,
        path: '/stock/stock-taking/detail',
        component: lazy(() =>
          import('./views/panels/stock/StockTaking/Detail'),
        ),
      },
      {
        exact: true,
        path: '/stock/stock-taking/detail/:stocktakingId',
        component: lazy(() =>
          import('./views/panels/stock/StockTaking/Detail'),
        ),
      },
      {
        exact: true,
        path: '/stock/budget',
        component: lazy(() => import('./views/panels/stock/Budget/index')),
      },
      {
        exact: true,
        path: '/stock/settings',
        component: lazy(() => import('./views/panels/stock/Settings/index')),
      },
      {
        exact: true,
        path: '/accounting/dashboard/overall',
        component: lazy(() =>
          import('./views/panels/accounting/dashboard/Overall'),
        ),
      },
      {
        exact: true,
        path: '/accounting/account/balance-sheet',
        component: lazy(() =>
          import('./views/panels/accounting/account/BalanceSheet'),
        ),
      },
      {
        exact: true,
        path: '/accounting/account/profit-loss',
        component: lazy(() =>
          import('./views/panels/accounting/account/ProfitLoss'),
        ),
      },
      {
        exact: true,
        path: '/accounting/account/general-ledger',
        component: lazy(() =>
          import('./views/panels/accounting/account/GeneralLedger'),
        ),
      },
      {
        exact: true,
        path: '/accounting/account/journal-entries-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/account/JournalEntriesDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/account/journal-entries',
        component: lazy(() =>
          import('./views/panels/accounting/account/JournalEntries'),
        ),
      },
      {
        exact: true,
        path: '/accounting/bill/bill-acceptance-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/bill/BillAcceptanceDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/bill/bill-acceptance-form',
        component: lazy(() =>
          import('./views/panels/accounting/bill/BillAcceptanceForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/account/journal-enties',
        component: lazy(() =>
          import('./views/panels/accounting/account/JournalEntries'),
        ),
      },
      {
        exact: true,
        path: '/accounting/account/trial-balance',
        component: lazy(() =>
          import('./views/panels/accounting/account/TrialBalance'),
        ),
      },
      {
        exact: true,
        path: '/accounting/configuration/chart-of-account',
        component: lazy(() =>
          import('./views/panels/accounting/account/ChartOfAccount'),
        ),
      },
      {
        exact: true,
        path: '/accounting/account/monthly-cash-in-out-summary',
        component: lazy(() =>
          import(
            './views/panels/accounting/account/MonthlyCashInOutSummary'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/payment/payment-voucher-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/payment/PaymentVoucherDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/payment/payment-voucher',
        component: lazy(() =>
          import('./views/panels/accounting/payment/PaymentVoucher'),
        ),
      },
      {
        exact: true,
        path: '/accounting/payment/combined-payment-note-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/payment/CombinedPaymentNoteDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/payment/combined-payment-note-form',
        component: lazy(() =>
          import(
            './views/panels/accounting/payment/CombinedPaymentNoteForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/payment/ap-payment-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/payment/APPaymentDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/payment/ap-payment-form',
        component: lazy(() =>
          import('./views/panels/accounting/payment/APPaymentForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/payment/receipt-acceptance-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/payment/ReceiptAcceptanceDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/payment/account-payable-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/payment/AccountPayableDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/payment/account-payable-form',
        component: lazy(() =>
          import('./views/panels/accounting/payment/AccountPayableForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/receipt/receipt-voucher',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/ReceiptVoucher'),
        ),
      },
      {
        exact: true,
        path: '/accounting/receipt/receipt-voucher-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/receipt/ReceiptVoucherDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/receipt/receipt-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/ReceiptDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/receipt/receipt-form',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/ReceiptForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/withholding-tax',
        component: lazy(() =>
          import('./views/panels/accounting/withholding-tax/WithholdingTaxDocument'),
        ),
      },

      {
        exact: true,
        path: '/accounting/billing-detail/receipt',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/ReceiptDetail'),
        ),
      },
      {
        exact: true,
        path: '/accounting/billing-detail/invoice',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/InvoiceDetail'),
        ),
      },
      {
        exact: true,
        path: '/accounting/invoice/invoice-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/invoice/InvoiceDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/invoice/invoice-form',
        component: lazy(() =>
          import('./views/panels/accounting/invoice/InvoiceForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/credit-note/credit-note-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/credit-note/CreditNoteDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/credit-note/credit-note-form',
        component: lazy(() =>
          import('./views/panels/accounting/credit-note/CreditNoteForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/invoice/tax-invoice-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/invoice/TaxInvoiceDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/invoice/tax-invoice-form',
        component: lazy(() =>
          import('./views/panels/accounting/invoice/TaxInvoiceForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/debit-note/debit-note-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/debit-note/DebitNoteDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/debit-note/debit-note-form',
        component: lazy(() =>
          import('./views/panels/accounting/debit-note/DebitNoteForm'),
        ),
      },
      {
        exact: true,
        path:
          '/accounting/account-receivable/account-receivable-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/account-receivable/AccountReceivableDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/account-receivable/account-receivable-form',
        component: lazy(() =>
          import(
            './views/panels/accounting/account-receivable/AccountReceivableForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/account-receivable/debtor-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/account-receivable/DebtorDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/account-receivable/debtor-form',
        component: lazy(() =>
          import(
            './views/panels/accounting/account-receivable/DebtorForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/receive-payment/receive-payment-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/receive-payment/ReceivePaymentDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/receive-payment/receive-payment-form',
        component: lazy(() =>
          import(
            './views/panels/accounting/receive-payment/ReceivePaymentForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/billing-note/billing-note-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/billingNote/BillingNoteDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/billing-note/billing-note-form',
        component: lazy(() =>
          import('./views/panels/accounting/billingNote/BillingNoteForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/deposit/deposit-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/deposit/DepositDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/deposit/deposit-form',
        component: lazy(() =>
          import('./views/panels/accounting/deposit/DepositForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/deposit/deposit-payment-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/deposit/DepositPaymentDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/deposit/deposit-payment-form',
        component: lazy(() =>
          import('./views/panels/accounting/deposit/DepositPaymentForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/billing-detail/quotation-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/billing-detail/QuotationDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/billing-detail/quotation',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/QuotationDetail'),
        ),
      },
      {
        exact: true,
        path: '/accounting/billing-detail/tax-invoice',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/TaxInvoiceDetail'),
        ),
      },
      {
        exact: true,
        path: '/accounting/invoice-doc',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/InvoiceDoc'),
        ),
      },
      {
        exact: true,
        path: '/accounting/billing-detail/purchase-order',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/PurchaseOrderDetail'),
        ),
      },
      {
        exact: true,
        path: '/accounting/billing-note',
        component: lazy(() =>
          import('./views/panels/accounting/receipt/BillingNote'),
        ),
      },
      {
        exact: true,
        path: '/accounting/expense/invoice-create',
        component: lazy(() =>
          import('./views/panels/accounting/expense/InvoiceCreate'),
        ),
      },
      {
        exact: true,
        path: '/accounting/expense/expense-record-dashboard',
        component: lazy(() =>
          import(
            './views/panels/accounting/expense/ExpenseRecordDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/expense/expense-record-form',
        component: lazy(() =>
          import('./views/panels/accounting/expense/ExpenseRecordForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/expense/purchase-order',
        component: lazy(() =>
          import('./views/panels/accounting/expense/PurchaseOrder'),
        ),
      },
      {
        exact: true,
        path: '/accounting/expense/purchase-fixed-asset-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/expense/FixedAssetDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/expense/purchase-fixed-asset-form',
        component: lazy(() =>
          import('./views/panels/accounting/expense/FixedAssetForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/expense/fixed-asset-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/account/FixedAssetDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/expense/fixed-asset-form',
        component: lazy(() =>
          import('./views/panels/accounting/account/FixedAssetForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/purchase-order-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/purchase-order/PurchaseOrderDashboard'),
        ),
      },
      {
        exact: true,
        path: [
          '/accounting/purchase-requisition',
          '/hospital/purchase-requisition',
        ],
        component: lazy(() =>
          import(
            './views/panels/accounting/purchase-requisition/PurchaseRequisitionDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: [
          '/accounting/purchase-requisition/form',
          '/hospital/purchase-requisition/form',
        ],
        component: lazy(() =>
          import(
            './views/panels/accounting/purchase-requisition/PurchaseRequisitionForm'
          ),
        ),
      },
      {
        exact: true,
        path: [
          '/accounting/purchase-requisition/form/:id',
          '/hospital/purchase-requisition/form/:id',
        ],
        component: lazy(() =>
          import(
            './views/panels/accounting/purchase-requisition/PurchaseRequisitionForm'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/purchase-order/purchase-order-form',
        component: lazy(() =>
          import('./views/panels/accounting/purchase-order/PurchaseOrderForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/purchase-order/form',
        component: lazy(() =>
          import('./views/panels/accounting/purchase-order/PurchaseOrderForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/purchase-order/form/:purchaseOrderId',
        component: lazy(() =>
          import('./views/panels/accounting/purchase-order/PurchaseOrderForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/finance/cash-bank-wallet-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/finance/CashBankWalletDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/finance/payment-transaction-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/finance/PaymentTransactionDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/finance/advance-payment-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/finance/AdvancePaymentDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/finance/cheque-receive-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/finance/ChequeReceiveDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/finance/withholding-tax-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/finance/WithholdingTaxDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/finance/cheque-expense-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/finance/ChequeExpenseDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/stock/purchase-order',
        component: lazy(() =>
          import('./views/panels/stock/Accounting/PurchaseOrder/index'),
        ),
      },
      {
        exact: true,
        path: '/accounting/stock/purchase-order/form',
        component: lazy(() =>
          import('./views/panels/stock/Accounting/PurchaseOrder/OrderForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/stock/purchase-order/form/:purchaseOrderId',
        component: lazy(() =>
          import('./views/panels/stock/Accounting/PurchaseOrder/OrderForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/creditor-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/creditor'),
        ),
      },
      {
        exact: true,
        path: '/accounting/creditor-dashboard/form',
        component: lazy(() =>
          import('./views/panels/accounting/creditor/CreditorForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/creditor-dashboard/form/:creditorId',
        component: lazy(() =>
          import('./views/panels/accounting/creditor/CreditorForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/company/form',
        component: lazy(() =>
          import('./views/panels/stock/Company/CompanyForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/company/form/:supplierId',
        component: lazy(() =>
          import('./views/panels/stock/Company/CompanyForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/goods-receipt',
        component: lazy(() =>
          import('./views/panels/accounting/order-receipt/OrderReceiptDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/back-office/goods-receipt',
        component: lazy(() =>
          import('./views/panels/accounting/order-receipt/BackOfficeReceiptList'),
        ),
      },
      {
        exact: true,
        path: '/accounting/back-office/order-receipt/form',
        component: lazy(() =>
          import('./views/panels/accounting/order-receipt/BackOfficeReceiptForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/back-office/order-receipt/form/:orderReceiptId',
        component: lazy(() =>
          import('./views/panels/accounting/order-receipt/BackOfficeReceiptForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/order-receipt/form',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseReceipt/ReceiptForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/order-receipt/form/:orderReceiptId',
        component: lazy(() =>
          import('./views/panels/stock/PurchaseReceipt/ReceiptForm'),
        ),
      },
      {
        exact: true,
        path: '/accounting/goods-issue',
        component: lazy(() =>
          import('./views/panels/accounting/goods-issue/GoodsIssueDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/goods-issue/manage',
        component: lazy(() => import('./views/panels/stock/GoodsIssue/Manage')),
      },
      {
        exact: true,
        path: '/accounting/goods-issue/manage/:goodsIssueId',
        component: lazy(() => import('./views/panels/stock/GoodsIssue/Manage')),
      },
      {
        exact: true,
        path: '/accounting/inventory-management',
        component: lazy(() =>
          import('./views/panels/stock/InventoryManagement'),
        ),
      },
      {
        exact: true,
        path: '/accounting/inventory-management/detail',
        component: lazy(() =>
          import('./views/panels/stock/InventoryManagement/Detail'),
        ),
      },
      {
        exact: true,
        path:
          '/accounting/inventory-management/detail/:productId/:warehouseId',
        component: lazy(() =>
          import('./views/panels/stock/InventoryManagement/Detail'),
        ),
      },
      {
        exact: true,
        path:
          '/accounting/inventory-management/detail/:productId/:warehouseId/reconcile-inventory/:inventoryLotId',
        component: lazy(() =>
          import(
            './views/panels/stock/InventoryManagement/ReconcileInventory'
          ),
        ),
      },
      {
        exact: true,
        path: '/accounting/product/product-dashboard',
        component: lazy(() =>
          import('./views/panels/accounting/product/ProductDashboard'),
        ),
      },
      {
        exact: true,
        path: '/accounting/stock-settings',
        component: lazy(() =>
          import('./views/panels/accounting/product/settings'),
        ),
      },
      {
        exact: true,
        path: '/helpdesk/hd-dashboard',
        component: lazy(() => import('./views/panels/helpdesk/Dashboard')),
      },
      {
        exact: true,
        path: '/helpdesk/hd-dashboard',
        component: lazy(() => import('./views/panels/helpdesk/Dashboard')),
      },
      {
        exact: true,
        path: '/helpdesk/hd-dashboard',
        component: lazy(() => import('./views/panels/helpdesk/Dashboard')),
      },
      {
        exact: true,
        path: '/helpdesk/hd-ticket',
        component: lazy(() => import('./views/panels/helpdesk/CreateTicket')),
      },
      {
        exact: true,
        path: '/helpdesk/hd-ticket-list',
        component: lazy(() => import('./views/panels/helpdesk/TicketList')),
      },
      {
        exact: true,
        path: '/users/notification',
        component: lazy(() => import('./views/panels/helpdesk/TicketList')),
      },
      {
        exact: true,
        path: '/users/todo',
        component: lazy(() => import('./views/users/todo/TodoDashboard')),
      },
      {
        exact: true,
        path: '/helpdesk/hd-ticket-details',
        component: lazy(() => import('./views/panels/helpdesk/TicketDetails')),
      },
      {
        exact: true,
        path: '/helpdesk/hd-customer-list',
        component: lazy(() => import('./views/panels/helpdesk/Customer')),
      },
      {
        exact: true,
        path: '/project-crm/pc-dashboard',
        component: lazy(() => import('./views/panels/project-crm/Dashboard')),
      },
      {
        exact: true,
        path: '/project-crm/pc-customers',
        component: lazy(() => import('./views/panels/project-crm/Customers')),
      },
      {
        exact: true,
        path: '/project-crm/pc-project',
        component: lazy(() => import('./views/panels/project-crm/Project')),
      },
      {
        exact: true,
        path: '/project-crm/pc-task',
        component: lazy(() => import('./views/panels/project-crm/Task')),
      },
      {
        exact: true,
        path: '/hospital/membership/mshp-dashboard',
        component: lazy(() => import('./views/panels/membership/Dashboard')),
      },
      {
        exact: true,
        path: '/hospital/membership/mshp-email',
        component: lazy(() => import('./views/panels/membership/Email')),
      },
      {
        exact: true,
        path: '/hospital/membership/mshp-country',
        component: lazy(() => import('./views/panels/membership/Country')),
      },
      {
        exact: true,
        path: '/hospital/membership/mshp-coupons',
        component: lazy(() => import('./views/panels/membership/Coupons')),
      },
      {
        exact: true,
        path: '/hospital/membership/mshp-newsletter',
        component: lazy(() => import('./views/panels/membership/Newsletter')),
      },
      {
        exact: true,
        path: '/hospital/membership/mshp-user',
        component: lazy(() => import('./views/panels/membership/User')),
      },
      {
        exact: true,
        path: '/hospital/membership/mshp-membership',
        component: lazy(() => import('./views/panels/membership/Membership')),
      },
      {
        exact: true,
        path: '/school/sch-dashboard',
        component: lazy(() => import('./views/panels/school/Dashboard')),
      },
      {
        exact: true,
        path: '/school/sch-student',
        component: lazy(() => import('./views/panels/school/Student')),
      },
      {
        exact: true,
        path: '/school/sch-parents',
        component: lazy(() => import('./views/panels/school/Parents')),
      },
      {
        exact: true,
        path: '/school/sch-teacher',
        component: lazy(() => import('./views/panels/school/Teacher')),
      },
      {
        exact: true,
        path: '/sis/sis-dashboard',
        component: lazy(() => import('./views/panels/sis/Dashboard')),
      },
      {
        exact: true,
        path: '/sis/sis-leave',
        component: lazy(() => import('./views/panels/sis/Leave')),
      },
      {
        exact: true,
        path: '/sis/sis-evaluation',
        component: lazy(() => import('./views/panels/sis/Evaluation')),
      },
      {
        exact: true,
        path: '/sis/sis-event',
        component: lazy(() => import('./views/panels/sis/Event')),
      },
      {
        exact: true,
        path: '/sis/sis-circular',
        component: lazy(() => import('./views/panels/sis/Circular')),
      },
      {
        exact: true,
        path: '/sis/sis-course',
        component: lazy(() => import('./views/panels/sis/Course')),
      },
      {
        exact: true,
        path: '/crypto/cp-dashboard',
        component: lazy(() => import('./views/panels/crypto/Dashboard')),
      },
      {
        exact: true,
        path: '/crypto/cp-exchange',
        component: lazy(() => import('./views/panels/crypto/Exchange')),
      },
      {
        exact: true,
        path: '/crypto/cp-wallet',
        component: lazy(() => import('./views/panels/crypto/Wallet')),
      },
      {
        exact: true,
        path: '/crypto/cp-transactions',
        component: lazy(() => import('./views/panels/crypto/Transaction')),
      },
      {
        exact: true,
        path: '/crypto/cp-history',
        component: lazy(() => import('./views/panels/crypto/History')),
      },
      {
        exact: true,
        path: '/crypto/cp-trading',
        component: lazy(() => import('./views/panels/crypto/Trading')),
      },
      {
        exact: true,
        path: '/crypto/cp-coin',
        component: lazy(() => import('./views/panels/crypto/InitialCoin')),
      },
      {
        exact: true,
        path: '/crypto/cp-ico',
        component: lazy(() => import('./views/panels/crypto/IcoListing')),
      },
      {
        exact: true,
        path: '/ecommerce/ecomm-product',
        component: lazy(() => import('./views/panels/ecommerce/Product')),
      },
      {
        exact: true,
        path: '/ecommerce/ecomm-product-details',
        component: lazy(() =>
          import('./views/panels/ecommerce/ProductDetails'),
        ),
      },
      {
        exact: true,
        path: '/ecommerce/ecomm-order',
        component: lazy(() => import('./views/panels/ecommerce/Order')),
      },
      {
        exact: true,
        path: '/ecommerce/ecomm-checkout',
        component: lazy(() => import('./views/panels/ecommerce/Checkout')),
      },
      {
        exact: true,
        path: '/ecommerce/ecomm-cart',
        component: lazy(() => import('./views/panels/ecommerce/Cart')),
      },
      {
        exact: true,
        path: '/ecommerce/ecomm-customer',
        component: lazy(() => import('./views/panels/ecommerce/Customers')),
      },
      {
        exact: true,
        path: '/ecommerce/ecomm-seller',
        component: lazy(() => import('./views/panels/ecommerce/Sellers')),
      },
      {
        exact: true,
        path: '/hospital/test/advance/sweet-alert',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceAlert'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/datepicker',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceDatepicker'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/task-board',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceTaskBoard'),
        ),
      },
      {
        exact: true,
        path: '/users/task-board',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceTaskBoard'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/light-box',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceLightBox'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/adv-modal',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceModal'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/notification',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceNotification'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/nestable',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceNestable'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/p-notify',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvancePNotify'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/rating',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceRating'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/range-slider',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceRangeSlider'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/slider',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceSlider'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/syntax-highlighter',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceSyntaxHighlighter'),
        ),
      },
      {
        exact: true,
        path: '/hospital/test/advance/tour',
        component: lazy(() =>
          import('./views/ui-elements/advance/AdvanceTour'),
        ),
      },
      {
        exact: true,
        path: '/forms/form-basic',
        component: lazy(() => import('./views/forms/FormsElements')),
      },
      {
        exact: true,
        path: '/forms/form-advance',
        component: lazy(() => import('./views/forms/FormsAdvance')),
      },
      {
        exact: true,
        path: '/forms/form-validation',
        component: lazy(() => import('./views/forms/FormsValidation')),
      },
      {
        exact: true,
        path: '/forms/form-masking',
        component: lazy(() => import('./views/forms/FormsMasking')),
      },
      {
        exact: true,
        path: '/forms/form-wizard',
        component: lazy(() => import('./views/forms/FormsWizard')),
      },
      {
        exact: true,
        path: '/hospital/forms/form-picker',
        component: lazy(() => import('./views/forms/FormsPicker')),
      },
      {
        exact: true,
        path: '/forms/form-select',
        component: lazy(() => import('./views/forms/FormsSelect')),
      },
      {
        exact: true,
        path: '/tables/bootstrap',
        component: lazy(() => import('./views/tables/BootstrapTable')),
      },
      {
        exact: true,
        path: '/table/datatable/tbl-basic',
        component: lazy(() => import('./views/tables/react-table/Basic')),
      },
      {
        exact: true,
        path: '/table/datatable/tbl-footer',
        component: lazy(() => import('./views/tables/react-table/Footers')),
      },
      {
        exact: true,
        path: '/table/datatable/tbl-sorting',
        component: lazy(() => import('./views/tables/react-table/Sorting')),
      },
      {
        exact: true,
        path: '/table/datatable/tbl-filtering',
        component: lazy(() => import('./views/tables/react-table/Filtering')),
      },
      {
        exact: true,
        path: '/table/datatable/tbl-grouping',
        component: lazy(() => import('./views/tables/react-table/Grouping')),
      },
      {
        exact: true,
        path: '/table/datatable/tbl-pagination',
        component: lazy(() => import('./views/tables/react-table/Pagination')),
      },
      {
        exact: true,
        path: '/charts/apex-chart',
        component: lazy(() => import('./views/charts/apex-chart')),
      },
      {
        exact: true,
        path: '/charts/chart-js',
        component: lazy(() => import('./views/charts/chart-js')),
      },
      {
        exact: true,
        path: '/charts/highchart',
        component: lazy(() => import('./views/charts/highchart')),
      },
      {
        exact: true,
        path: '/charts/rechart',
        component: lazy(() => import('./views/charts/re-chart')),
      },
      {
        exact: true,
        path: '/maps/google-map',
        component: lazy(() => import('./views/maps/GoogleMaps')),
      },
      {
        exact: true,
        path: '/maps/vector-map',
        component: lazy(() => import('./views/maps/VectorMaps')),
      },
      {
        exact: true,
        path: '/todo/todo-basic',
        component: lazy(() => import('./views/applications/to-do/ToDo')),
      },
      {
        exact: true,
        path: '/task/task-list',
        component: lazy(() => import('./views/applications/task/TaskList')),
      },
      {
        exact: true,
        path: '/task/task-board',
        component: lazy(() => import('./views/applications/task/TaskBoard')),
      },
      {
        exact: true,
        path: '/task/task-detail',
        component: lazy(() => import('./views/applications/task/TaskDetails')),
      },
      {
        exact: true,
        path: '/gallery/gallery-grid',
        component: lazy(() =>
          import('./views/applications/gallery/GalleryGrid'),
        ),
      },
      {
        exact: true,
        path: '/gallery/photo-grid',
        component: lazy(() => import('./views/applications/gallery/PhotoGrid')),
      },
      {
        exact: true,
        path: '/gallery/gallery-masonry',
        component: lazy(() =>
          import('./views/applications/gallery/MasonryGallery'),
        ),
      },
      {
        exact: true,
        path: '/editor/ck-editor/ck-classic',
        component: lazy(() =>
          import('./views/extensions/editors/ck-editor/EditorCkClassic'),
        ),
      },
      {
        exact: true,
        path: '/editor/ck-editor/ck-balloon',
        component: lazy(() =>
          import('./views/extensions/editors/ck-editor/EditorCkBalloon'),
        ),
      },
      {
        exact: true,
        path: '/editor/ck-editor/ck-inline',
        component: lazy(() =>
          import('./views/extensions/editors/ck-editor/EditorCkInline'),
        ),
      },
      {
        exact: true,
        path: '/editor/ck-editor/ck-document',
        component: lazy(() =>
          import('./views/extensions/editors/ck-editor/EditorCkDocument'),
        ),
      },
      {
        exact: true,
        path: '/editor/rich-editor',
        component: lazy(() =>
          import('./views/extensions/editors/EditorRichNib'),
        ),
      },
      {
        exact: true,
        path: '/editor/jodit-wysiwyg',
        component: lazy(() =>
          import('./views/extensions/editors/EditorJoditWYSIWYG'),
        ),
      },
      {
        exact: true,
        path: '/image-cropper',
        component: lazy(() => import('./views/extensions/ImageCropper')),
      },
      {
        exact: true,
        path: '/file-upload',
        component: lazy(() => import('./views/extensions/FileUpload')),
      },
      {
        exact: true,
        path: '/hospital/full-calendar',
        component: lazy(() => import('./views/extensions/FullEventCalendar')),
      },
      {
        exact: true,
        path: '/invoice/invoice-basic',
        component: lazy(() =>
          import('./views/extensions/invoice/InvoiceBasic'),
        ),
      },
      {
        exact: true,
        path: '/invoice/invoice-summary',
        component: lazy(() =>
          import('./views/extensions/invoice/InvoiceSummary'),
        ),
      },
      {
        exact: true,
        path: '/invoice/invoice-list',
        component: lazy(() => import('./views/extensions/invoice/InvoiceList')),
      },
      {
        exact: true,
        path: '/sample-page',
        component: lazy(() => import('./views/extra/SamplePage')),
      },
      {
        exact: true,
        path: '/support/about-page',
        component: lazy(() => import('./views/panels/support/AboutPage')),
      },
      {
        exact: true,
        path: '/settings/about-page',
        component: lazy(() => import('./views/panels/support/AboutPage')),
      },
      {
        exact: true,
        path: '/settings/health-check-api-page',
        component: lazy(() =>
          import('./views/panels/support/HealthCheckApiPage'),
        ),
      },

      {
        exact: true,
        path: '/designer/form',
        component: lazy(() => import('./designer/system/form')),
      },
      {
        exact: true,
        path: '/designer/form/:formCode',
        component: lazy(() => import('./designer/system/form')),
      },
      {
        exact: true,
        path: '/display/form/:formCode',
        component: lazy(() => import('./designer/display/form')),
      },
      {
        exact: true,
        path: '/designer/display/form/:formCode',
        component: lazy(() => import('./designer/display/form')),
      },
      {
        exact: true,
        path: '/:perspective/display/form/:formCode',
        component: lazy(() => import('./designer/display/form')),
      },
      {
        exact: true,
        path: '/display/favorite/:favoriteCode',
        component: lazy(() => import('./designer/display/favorite')),
      },
      {
        exact: true,
        path: '/designer/display/favorite/:favoriteCode',
        component: lazy(() => import('./designer/display/favorite')),
      },
      {
        exact: true,
        path: '/:perspective/display/favorite/:favoriteCode',
        component: lazy(() => import('./designer/display/favorite')),
      },
      {
        exact: true,
        path: '/designer/menu/form',
        component: lazy(() => import('./designer/menu/form')),
      },
      {
        exact: true,
        path: '/designer/menu/module',
        component: lazy(() => import('./designer/menu/module')),
      },
      {
        exact: true,
        path: '/designer/menu/page',
        component: lazy(() => import('./designer/menu/page')),
      },
      {
        exact: true,
        path: '/designer/menu/config',
        component: lazy(() => import('./designer/menu/config')),
      },
      {
        exact: true,
        path: '/designer/perspective/config',
        component: lazy(() => import('./designer/menu/config')),
      },
      {
        exact: true,
        path: '/designer/page',
        component: lazy(() => import('./designer/system/page')),
      },
      {
        exact: true,
        path: '/designer/page/:pageCode',
        component: lazy(() => import('./designer/system/page')),
      },
      {
        exact: true,
        path: [
          '/hospital/radiology/x-ray-dashboard',
          '/hospital/radiology/xray-dashboard',
          '/hospital/radiology/ultrasound-dashboard',
          '/hospital/radiology/ct-dashboard',
          '/hospital/radiology/mri-dashboard',
          '/hospital/radiology/mammography-dashboard',
        ],
        component: lazy(() =>
          import('./views/panels/hospital/RadiologyDashboard'),
        ),
      },
      {
        exact: true,
        path: '/display/page/:pageCode',
        component: lazy(() => import('./designer/display/page')),
      },
      {
        exact: true,
        path: '/designer/display/page/:pageCode',
        component: lazy(() => import('./designer/display/page')),
      },
      {
        exact: true,
        path: '/designer/converter',
        component: lazy(() => import('./designer/menu/converter')),
      },
      {
        exact: true,
        path: '/designer/menu/form-assessment',
        component: lazy(() => import('./designer/menu/assessment')),
      },
      {
        exact: true,
        path: '/designer/menu/form-assessment/:id/preview',
        component: lazy(() => import('./designer/menu/assessment/Preview')),
      },
      {
        exact: true,
        path: '/designer/menu/form-assessment/add',
        component: lazy(() => import('./designer/menu/assessment/AddForm')),
      },
      {
        exact: true,
        path: '/designer/menu/form-assessment/:id',
        component: lazy(() => import('./designer/menu/assessment/AddForm')),
      },
      {
        exact: true,
        path: '/designer/menu/widget',
        component: lazy(() => import('./designer/menu/widget')),
      },
      {
        exact: true,
        path: '/designer/widget',
        component: lazy(() => import('./designer/system/widget')),
      },
      {
        exact: true,
        path: '/designer/widget/:widgetCode',
        component: lazy(() => import('./designer/system/widget')),
      },
      {
        exact: true,
        path: '/display/widget/:widgetCode',
        component: lazy(() => import('./designer/display/widget')),
      },
      {
        exact: true,
        path: '/designer/display/widget/:widgetCode',
        component: lazy(() => import('./designer/display/widget')),
      },
      {
        exact: true,
        path: '/:perspective/display/widget/:widgetCode',
        component: lazy(() => import('./designer/display/widget')),
      },
      {
        exact: true,
        path: '/designer/menu/dashboard',
        component: lazy(() => import('./designer/menu/dashboard')),
      },
      {
        exact: true,
        path: '/designer/dashboard',
        component: lazy(() => import('./designer/system/dashboard')),
      },
      {
        exact: true,
        path: '/designer/dashboard/:dashboardCode',
        component: lazy(() => import('./designer/system/dashboard')),
      },
      {
        exact: true,
        path: '/display/dashboard/:dashboardCode',
        component: lazy(() => import('./designer/display/dashboard')),
      },
      {
        exact: true,
        path: '/designer/display/dashboard/:dashboardCode',
        component: lazy(() => import('./designer/display/dashboard')),
      },
      {
        exact: true,
        path: '/:perspective/display/dashboard/:dashboardCode',
        component: lazy(() => import('./designer/display/dashboard')),
      },
      {
        exact: true,
        path: '/checkup/package-dashboard',
        component: lazy(() =>
          import(
            './views/panels/hospital/Checkup/PackageDashboard'
          ),
        ),
      },
      {
        exact: true,
        path: '/hospital/display/page/page-medical-statistics',
        component: lazy(() =>
          import('./views/panels/hospital/MedicalStatistic')
        ),
      },
      {
        exact: true,
        path: '/hospital/opd-medical-statistics',
        component: lazy(() =>
          import('./views/panels/hospital/OpdMedicalStatistic')
        ),
      },
      {
        exact: true,
        path: '/hospital/display/page/page-radiology-dashboard',
        component: lazy(() =>
          import('./views/panels/hospital/RadiologyDashboard')
        ),
      },
      {
        exact: true,
        path: '/:perspective/display/page/:pageCode',
        component: lazy(() => import('./designer/display/page')),
      },
      {
        exact: true,
        path: '/patient/dashboard/analytics/:hn',
        component: lazy(() => import('./views/patient/DashAnalytics')),
      },
      {
        exact: true,
        path: '/patient/ipd/therapies-calendar/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/TherapiesCalendar/index'),
        ),
      },
      {
        exact: true,
        path: '/patient/ipd/e-mar/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/Emar/Patient/index'),
        ),
      },
      {
        exact: true,
        path: '/patient/ipd/consult/request',
        component: lazy(() =>
          import('./views/panels/hospital/Consult/ConsultList'),
        ),
      },
      {
        exact: true,
        path: '/patient/ipd/othermed/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/Medication/Admission/OtherMed'),
        ),
      },
      {
        exact: true,
        path: '/patient/info/prescription/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/Prescription/index'),
        ),
      },
      {
        exact: true,
        path: '/patient/info/medication/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/Medication/index'),
        ),
      },
      {
        exact: true,
        path: '/patient/info/order/:hn',
        component: lazy(() => import('./views/panels/hospital/Order/index')),
      },
      {
        exact: true,
        path: '/patient/info/vital-sign/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/index'),
        ),
      },
      {
        exact: true,
        path: '/patient/info/vital-sign-chart/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/VitalSignChart'),
        ),
      },
      {
        exact: true,
        path: '/patient/info/monitor-chart/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/VitalSign/MonitorChart'),
        ),
      },
      {
        exact: true,
        path: '/patient/info/hosp-module-history/:hn',
        component: lazy(() => import('./components/ModuleHistory/index')),
      },
      {
        exact: true,
        path: '/patient/info/hosp-images/:hn',
        component: lazy(() => import('./views/panels/hospital/Images/index')),
      },
      {
        exact: true,
        path: '/patient/info/hosp-document/:hn',
        component: lazy(() => import('./components/Document/DocumentViewer')),
      },
      {
        exact: true,
        path: '/patient/info/timeline/:hn',
        component: lazy(() => import('./views/panels/hospital/Timeline/index')),
      },
      {
        exact: true,
        path: '/patient/info/hosp-genogram/:hn',
        component: lazy(() => import('./views/panels/hospital/Genogram/index')),
      },
      {
        exact: true,
        path: '/patient/info/patient-evaluation/:hn',
        component: lazy(() => import('./views/patient/Evaluation/index')),
      },
      {
        exact: true,
        path: '/patient/info/hosp-invoice/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/Invoice/index'),
        ),
      },
      {
        exact: true,
        path: '/patient/info/hosp-dental/:hn',
        component: lazy(() => import('./views/panels/hospital/Dental/index')),
      },
      {
        exact: true,
        path: '/patient/info/dashboard/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/Dashboard/index'),
        ),
      },
      {
        exact: true,
        path: '/patient/opd/pe-classic/:hn',
        component: lazy(() =>
          import('./views/patient/Evaluation/Exam/PhysicalExamination'),
        ),
      },
      {
        exact: true,
        path: '/patient/opd/pe/:hn',
        component: lazy(() =>
          import('./views/patient/Evaluation/Exam/PhysicalExaminationV2'),
        ),
      },
      {
        exact: true,
        path: '/patient/info/assessment/:hn',
        component: lazy(() =>
          import('./views/panels/hospital/PatientAssessment/AssessmentArchive'),
        ),
      },
      {
        exact: true,
        path: '/patient/:view/:module/:submodule/:hn',
        component: lazy(() => import('./views/patient')),
      },
      {
        exact: false,
        path: '/test/testoffice',
        component: lazy(() => import('./views/panels/test/TestOnlyOffice')),
      },
      {
        exact: false,
        path: '/hospital/notification/alert-component',
        component: lazy(() =>
          import('./components/Notifications/AlertSubscribe'),
        ),
      },
      {
        exact: false,
        path: '/hospital/notification/patient-sticky-note',
        component: lazy(() =>
          import('./components/Notifications/PatientStickyNote'),
        ),
      },
      {
        exact: false,
        path: '/hospital/notification/benefit-sticky-note',
        component: lazy(() =>
          import('./components/Notifications/BenefitStickyNote'),
        ),
      },
      {
        exact: false,
        path: '/desinger/ehr/template-store/:templateId',
        component: lazy(() => import('./designer/ehr/template-store')),
      },
      {
        exact: true,
        path: '/desinger/ehr/template-store',
        component: lazy(() => import('./designer/ehr/template-store')),
      },
      {
        exact: false,
        path: '/designer/ehr/template-store/:templateId',
        component: lazy(() => import('./designer/ehr/template-store')),
      },
      {
        exact: true,
        path: '/designer/ehr/template-store',
        component: lazy(() => import('./designer/ehr/template-store')),
      },
      {
        exact: false,
        path: '/designer/test-component',
        component: lazy(() =>
          import('./designer/system/component/TestComponent'),
        ),
      },
      {
        exact: false,
        path: '/designer/teeth-component',
        component: lazy(() =>
          import('./designer/system/component/TeethComponent'),
        ),
      },
      {
        exact: false,
        path: '/designer/teeth-v2-component',
        component: lazy(() =>
          import('./designer/system/component/TeethV2Component'),
        ),
      },
      {
        exact: false,
        path: '/designer/allergy-component',
        component: lazy(() =>
          import('./designer/system/component/AllergyComponent'),
        ),
      },
      {
        exact: false,
        path: '/designer/dental-radiology-editor',
        component: lazy(() =>
          import('./views/panels/hospital/DentalRadiologyEditor'),
        ),
      },
      {
        exact: false,
        path: '/designer/sig-component',
        component: lazy(() =>
          import('./designer/system/component/SIGComponent'),
        ),
      },
      {
        exact: false,
        path: '/designer/patient-assessment',
        component: lazy(() =>
          import('./designer/system/component/PatientAssessment'),
        ),
      },
      {
        exact: false,
        path: '/designer/dropdown-button',
        component: lazy(() =>
          import('./components/DropdownMenus/DropdownDesigner'),
        ),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/export-design',
        component: lazy(() =>
          import('./views/panels/hospital/Export-dashboard/index'),
        ),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/export-design/new',
        component: lazy(() =>
          import('./views/panels/hospital/Exportdesign/index'),
        ),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/export-design/:id',
        component: lazy(() =>
          import('./views/panels/hospital/Exportdesign/index'),
        ),
      },
      {
        exact: false,
        path: '/admin-panel/test/debugnotification',
        component: lazy(() => import('./views/panels/test/DebugNotification')),
      },
      {
        exact: false,
        path: '/admin-panel/test/debug-alert-component',
        component: lazy(() =>
          import('./views/panels/test/DebugAlertSubscribe'),
        ),
      },
      {
        exact: false,
        path: '/admin-panel/test/audio-analyzer',
        component: lazy(() =>
          import('./views/panels/test/AudioAnalyzerTest'),
        ),
      },
      {
        exact: false,
        path: '/admin-panel/test/prompt-ui-creator',
        component: lazy(() =>
          import('./views/panels/test/PromptUiCreator'),
        ),
      },
      {
        exact: false,
        path: '/admin-panel/test/prompt-dashboard-creator',
        component: lazy(() =>
          import('./views/panels/test/PromptDashboardCreator'),
        ),
      },
      {
        exact: false,
        path: '/admin-panel/test/prompt-dashbaord-creator',
        component: lazy(() =>
          import('./views/panels/test/PromptDashboardCreator'),
        ),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/hosp-dashboard',
        component: lazy(() => import('./views/panels/hospital/BiDashboard')),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/hosp-report',
        component: lazy(() => import('./views/panels/hospital/Report/index')),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/hosp-report/:id',
        component: lazy(() => import('./views/panels/hospital/Report/AddForm')),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/hosp-report/previewExample/:code',
        component: lazy(() =>
          import('./views/panels/hospital/Report/preview/index'),
        ),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/hosp-report/preview/:id',
        component: lazy(() =>
          import('./views/panels/hospital/Report/preview/index'),
        ),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/hosp-report-classic',
        component: lazy(() => import('./views/panels/hospital/ClassicReport')),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/hosp-policy-config',
        component: lazy(() =>
          import('./views/panels/setting/PolicyConfig/index'),
        ),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/hosp-global-config',
        component: lazy(() =>
          import('./views/panels/setting/GlobalConfig/index'),
        ),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/hosp-report-classic/:id/revision/:revisionId',
        component: lazy(() =>
          import('./views/panels/hospital/ClassicReport/CreateReport'),
        ),
      },
      {
        path: '/admin-panel/hospital/hosp-report-classic/:id',
        component: lazy(() =>
          import('./views/panels/hospital/ClassicReport/CreateReport'),
        ),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/form-assessment',
        component: lazy(() => import('./designer/menu/assessment')),
      },
      {
        exact: true,
        guard: GuestGuard,
        path: '/admin-panel/hospital/form-assessment/:id/preview',
        component: lazy(() => import('./designer/menu/assessment/Preview')),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/form-assessment/add',
        component: lazy(() => import('./designer/menu/assessment/AddForm')),
      },
      {
        exact: true,
        path: '/admin-panel/hospital/form-assessment/:id',
        component: lazy(() => import('./designer/menu/assessment/AddForm')),
      },
      {
        exact: true,
        path: '/admin-panel/user-import-excel',
        component: lazy(() =>
          import('./views/panels/admin-panel/UserImportExcel'),
        ),
      },
      {
        path: '/admin-panel/api-designer',
        component: lazy(() =>
          import('./views/panels/admin-panel/api-designer'),
        ),
      },
      {
        path: '/admin-panel/device-gateway/hl7-routes',
        component: lazy(() =>
          import('./views/panels/admin-panel/DeviceGateway'),
        ),
      },
      {
        path: '/admin-panel/device-gateway',
        component: lazy(() =>
          import('./views/panels/admin-panel/DeviceGateway'),
        ),
      },
      {
        path: '/admin-panel/hospital-layout',
        component: lazy(() =>
          import('./views/panels/admin-panel/HospitalLayout'),
        ),
      },
      {
        path: '/admin-panel/group-management',
        component: lazy(() =>
          import('./views/panels/admin-panel/AccessManagement/GroupManagement'),
        ),
      },
      {
        path: '/admin-panel/org-theme',
        component: lazy(() => import('./views/panels/admin-panel/OrgTheme')),
      },
      {
        path: '/admin-panel/folder-management',
        component: lazy(() =>
          import('./views/panels/admin-panel/FolderManagement'),
        ),
      },
      {
        path: '/admin-panel/backup-management',
        component: lazy(() =>
          import('./views/panels/admin-panel/BackupManagement'),
        ),
      },
      {
        path: '/admin-panel/debug-iv',
        component: lazy(() =>
          import('./views/panels/hospital/Order/IVFluidTable'),
        ),
      },
      {
        exact: true,
        path: '/hospital/patient-billing',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/BillingSummary'),
        ),
      },
      {
        exact: true,
        path: '/hospital/p',
        component: lazy(() =>
          import('./views/panels/hospital/Prescription/Scanner'),
        ),
      },
      {
        exact: true,
        path: '/hospital/p/:id',
        component: lazy(() =>
          import('./views/panels/hospital/Prescription/Scanner/TaskModal'),
        ),
      },
      {
        path: '/hospital/ipd-order/lab-order-component',
        exact: true,
        component: lazy(() =>
          import('./views/panels/hospital/Lab/components/LabComponent'),
        ),
      },
      {
        exact: true,
        path: '/hospital/patient-billing-summary',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/PatientBilling'),
        ),
      },
      {
        exact: true,
        path: '/hospital/benefits-allocation',
        component: lazy(() =>
          import('./views/panels/hospital/Finance/BenefitsAllocation'),
        ),
      },
      {
        path: '/',
        exact: true,
        component: () => <Redirect to={BASE_URL} />,
        //component: lazy(() => import("./views/panels/support/WelcomePage")),
      },
      {
        path: '*',
        exact: true,
        component: lazy(() => import('./views/panels/support/WelcomePage')),
      },
    ],
  },
]

export default routes
